import { isNumber, isSchemaRecord, isString } from './runtime-values.js';
import type { SchemaInput } from './schema-input.js';

export interface TravelTimeArea {
  kind: 'travel_time';
  origin: { lat: number; lng: number };
  profile: 'driving-car' | 'foot-walking' | 'cycling-regular';
  minutes: number;
  source: 'openrouteservice';
  geometryFingerprint: string;
  edited?: true;
}

export function travelTimeGeometryFingerprint(points: Array<{ lat: number; lng: number }>): string {
  let hash = 2166136261;
  for (const point of points) {
    for (const coordinate of [point.lat, point.lng]) {
      hash = Math.imul(hash ^ Math.round(coordinate * 1e5), 16777619);
    }
  }
  return `${points.length}:${hash >>> 0}`;
}

function travelTimeOrigin(value: SchemaInput): TravelTimeArea['origin'] | undefined {
  if (!isSchemaRecord(value) || !isNumber(value.lat) || !Number.isFinite(value.lat) || Math.abs(value.lat) > 90
    || !isNumber(value.lng) || !Number.isFinite(value.lng) || Math.abs(value.lng) > 180) return undefined;
  return { lat: value.lat, lng: value.lng };
}

export function parseTravelTimeArea(value: SchemaInput): TravelTimeArea | undefined {
  if (!isSchemaRecord(value) || value.kind !== 'travel_time' || value.source !== 'openrouteservice'
    || !isNumber(value.minutes) || !Number.isInteger(value.minutes) || value.minutes < 5 || value.minutes > 60
    || !isString(value.geometryFingerprint) || !/^\d{1,6}:\d{1,10}$/u.test(value.geometryFingerprint)) return undefined;
  const origin = travelTimeOrigin(value.origin);
  if (!origin) return undefined;
  if (value.profile !== 'driving-car' && value.profile !== 'foot-walking' && value.profile !== 'cycling-regular') return undefined;
  const area: TravelTimeArea = { kind: 'travel_time', source: 'openrouteservice', origin,
    profile: value.profile, minutes: value.minutes, geometryFingerprint: value.geometryFingerprint };
  if (value.edited === true) area.edited = true;
  return area;
}

export function travelTimeForPoints(value: TravelTimeArea | undefined, points: Array<{ lat: number; lng: number }>): TravelTimeArea | undefined {
  if (!value) return undefined;
  const area: TravelTimeArea = { ...value, origin: { ...value.origin } };
  if (value.geometryFingerprint !== travelTimeGeometryFingerprint(points)) area.edited = true;
  return area;
}

export function applyTravelTimeArea(stroke: { tool: string; points: Array<{ lat: number; lng: number }>; travelTime?: TravelTimeArea }, value: SchemaInput): void {
  if (stroke.tool !== 'polygon') return;
  const area = travelTimeForPoints(parseTravelTimeArea(value), stroke.points);
  if (area) stroke.travelTime = area;
}
